import { computed, ComputedRef, onMounted, onUnmounted, ref } from 'vue';
import { AdapterService } from '../adapter';
import {
  CreateTimerRecordModel,
  EditTimerRecordModel,
  GetTimerRecordsModel,
  StartTimerModel,
  StopTimerModel,
  TimerRecordModel,
} from '../models';
import {
  TimerTrackerEditRecordModel,
  TimeTrackerCreateRecordModel,
  TimeTrackerRecordModel,
  TimeTrackerUserModel,
} from '../../task-time-tracker';
import { TirPmNotificationEventBus, TirPmNotificationModel } from 'tir-components';

type UseTaskTimerParams = {
  /**ID задачи */
  taskId: ComputedRef<number | undefined>;
  /**Коллбек при успешном создании записи таймера */
  onSuccessCreateTimerRecord?: () => void;
  /**Коллбек при успешном редактировании записи таймера */
  onSuccessEditTimerRecord?: () => void;
  /**Открыть подтверждение запуска таймера */
  onOpenConfirmStartTimer?: () => void;
};

export const useTaskTimer = (params: UseTaskTimerParams) => {
  /**Адаптер */
  const adapter = new AdapterService();
  /**Дата старта таймера */
  const timerStartDate = ref<Date>();
  /**ID текущего таймера */
  const currentTimerId = ref<number>();
  /**Загрузка в кнопке таймера */
  const isLoadingTimerButton = ref(false);
  /**Список таймеров */
  const timerRecords = ref(new Array<TimerRecordModel>());
  /**Запустили таймер */
  const isStartTimer = ref(false);
  /**Список записей таймера для компонента */
  const timerTrackerRecords = computed(() =>
    timerRecords.value?.map(
      (record) =>
        new TimeTrackerRecordModel({
          TimerId: record.TimerId,
          DateStart: record.DateStart,
          DateEnd: record.DateEnd,
          Performer: record.Performer
            ? new TimeTrackerUserModel({
                Id: record.Performer.Id,
                FirstName: record.Performer.FirstName,
                LastName: record.Performer.LastName,
                MiddleName: record.Performer.MiddleName,
                AvatarColor: record.Performer.AvatarColor,
                Foto: record.Performer.Foto,
              })
            : undefined,
        }),
    ),
  );

  /**Получить текущий запущенный таймер */
  const getCurentTimerDateStart = async () => {
    if (!params?.taskId?.value) return;

    const resp = await adapter.getCurrentTimerDateStart(params.taskId.value);
    if (resp.IsSuccess && resp.Value) {
      timerStartDate.value = resp.Value.DateStart;
      currentTimerId.value = resp.Value.TimerId;
    }
  };

  /**Получить список таймеров */
  const getTimerRecords = async () => {
    if (!params?.taskId?.value) return;

    const resp = await adapter.getTimerRecords(
      new GetTimerRecordsModel({
        TaskId: params.taskId.value,
        IsOnlyEnded: true,
      }),
    );

    if (resp.IsSuccess) {
      timerRecords.value = resp.Value;
      if (isStartTimer.value) timerStartDate.value = new Date();
    }
  };

  /**Запуск таймера */
  const startTimer = async (isConfirm: boolean) => {
    if (!params?.taskId?.value) return;
    const otherTimerResp = await adapter.checkStartedOtherTimer(params.taskId.value);

    // Запущен другой таймер
    if (otherTimerResp.IsSuccess && otherTimerResp.Value) {
      // Если все таки необходимо запустить таймер - останавливаем запущенный
      if (isConfirm) {
        await adapter.stopTimer(
          new StopTimerModel({
            TimerId: otherTimerResp.Value,
          }),
        );
      } else {
        params.onOpenConfirmStartTimer?.();
        return;
      }
    }

    const resp = await adapter.startTimer(new StartTimerModel({ TaskId: params.taskId.value }));
    if (resp.IsSuccess) {
      isStartTimer.value = true;
      currentTimerId.value = resp.Value;
      timerStartDate.value = new Date();
    }
  };

  /**Остановить таймер */
  const stopTimer = async () => {
    if (!currentTimerId.value || !params?.taskId?.value) return;
    const resp = await adapter.stopTimer(
      new StopTimerModel({
        TimerId: currentTimerId.value,
      }),
    );
    document.dispatchEvent(
      new CustomEvent('stopTimer', {
        detail: { taskId: params.taskId.value, isSuccess: resp.IsSuccess },
      }),
    );
    if (resp.IsSuccess) {
      isStartTimer.value = false;
      currentTimerId.value = undefined;
      timerStartDate.value = undefined;
    }
  };
  /**Создать запись таймера */
  const createTimerRecord = async (data: TimeTrackerCreateRecordModel) => {
    if (!params?.taskId?.value) return;
    isLoadingTimerButton.value = true;
    const resp = await adapter.createTimerRecord(
      new CreateTimerRecordModel({
        TaskId: params.taskId.value,
        DateStart: data.DateStart,
        DateEnd: data.DateEnd,
      }),
    );
    if (resp.IsSuccess) {
      await getTimerRecords();
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Запись таймера успешно добавлена',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
      params?.onSuccessCreateTimerRecord?.();
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при добавлении записи таймера',
          type: 'error',
          group: 'general',
          life: 3000,
          closable: true,
        }),
      );
    }
    isLoadingTimerButton.value = false;
  };

  /**Редактировать запись таймера */
  const editTimerRecord = async (data: TimerTrackerEditRecordModel) => {
    if (!params?.taskId?.value) return;
    isLoadingTimerButton.value = true;
    const resp = await adapter.editTimerRecord(
      new EditTimerRecordModel({
        TimerId: data.TimerId,
        DateStart: data.DateStart,
        DateEnd: data.DateEnd,
      }),
    );
    if (resp.IsSuccess) {
      await getTimerRecords();
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Запись таймера успешно отредактированна',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
      params?.onSuccessEditTimerRecord?.();
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при редактировании записи таймера',
          type: 'error',
          group: 'general',
          life: 3000,
          closable: true,
        }),
      );
    }
    isLoadingTimerButton.value = false;
  };

  /**Удалить запись таймера */
  const deleteTimerRecord = async (timerId: number) => {
    if (!params?.taskId?.value) return;
    const resp = await adapter.deleteTimerRecord(timerId);
    if (resp.IsSuccess) {
      await getTimerRecords();
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Запись таймера успешно удалена',
          type: 'success',
          group: 'general',
          life: 2000,
          closable: true,
        }),
      );
    } else {
      TirPmNotificationEventBus.emit(
        'add',
        new TirPmNotificationModel({
          title: 'Произошла ошибка при удалении записи таймера',
          type: 'error',
          group: 'general',
          life: 3000,
          closable: true,
        }),
      );
    }
  };

  /**Обработчик при событии остановки таймера */
  const onStopTimer = async (event: any) => {
    if (!event?.detail?.taskId) return;
    const taskId = event.detail.taskId as number;

    if (taskId === params.taskId.value) {
      timerStartDate.value = undefined;
      timerRecords.value = new Array<TimerRecordModel>();
      await getTimerRecords();
    }
  };

  /**Инициализация данных таймера для задачи */
  const initTimerData = async () => {
    if (!params?.taskId?.value) return;
    await Promise.all([getTimerRecords(), getCurentTimerDateStart()]);
  };

  /**Сброс данных */
  const resetTimerData = () => {
    timerStartDate.value = undefined;
    isLoadingTimerButton.value = false;
    timerRecords.value = new Array<TimerRecordModel>();
  };

  onMounted(() => {
    document.addEventListener('stopTimer', onStopTimer);
  });

  onUnmounted(() => {
    document.removeEventListener('stopTimer', onStopTimer);
  });

  return {
    /**Дата старта таймера */
    timerStartDate,
    /**Загрузка в кнопке таймера */
    isLoadingTimerButton,
    /**Список записей таймера для компонента */
    timerTrackerRecords,
    /**Запуск таймера */
    startTimer,
    /**Остановить таймер */
    stopTimer,
    /**Создать запись таймера */
    createTimerRecord,
    /**Редактировать запись таймера */
    editTimerRecord,
    /**Удалить запись таймера */
    deleteTimerRecord,
    /**Инициализация данных таймера для задачи */
    initTimerData,
    /**Сброс данных */
    resetTimerData,
    /**Получить текущий запущенный таймер */
    getCurentTimerDateStart,
    getTimerRecords,
  };
};
